import { useRef, useMemo, useEffect, useState } from 'react';
import { Card, Stack, Box, Typography, Switch  } from '@mui/material';
import { usePageView } from 'src/hooks/use-page-view';
import { useSelection } from 'src/hooks/use-selection';
import ListSearch from 'src/components/ui/search/list-search';
import { ListTable } from './list-table';
import { ListView } from './list-view';
import { useTranslation } from 'react-i18next';
import { tokens } from 'src/locales/tokens';
import { useRouter } from 'src/hooks/use-router';
import { nextNavigate } from 'src/utils/navigation-utils';
import { paths } from 'src/paths';
import TitleButton from 'src/components/ui/headers/title-button';
import { useGroupHandler } from 'src/hooks/use-group-handler';
import PageSection from 'src/layouts/page-section';
import DropdownTracking from 'src/components/dropdown/dropdown-tracking';
import SearchSelectField from 'src/components/widgets/search-select-field';
import useDeviceView from 'src/hooks/use-device-view';

const getFilterValue = (filters, key) => {
  const value = filters?.query?.[key];
  if (typeof value !== 'string') return null;

  const match = value.match(/\[(.*?)\]\[(.*?)\]/);
  return match ? { value: match[1], label: match[2] } : null;
};

const Page = ({ dataSearch, dataStore, dataIds, dataCache }) => {
  const { t } = useTranslation();
  const { isPhone } = useDeviceView();
  const rootRef = useRef(null);
  const trackingRef = useRef();
  const statusRef = useRef();
  const router = useRouter();
  const dataSelection = useSelection(dataIds);

  const [options, setOptions] = useState([]);
  const [hintValueStatus, setHintValueStatus] = useState('');

  usePageView();

  const labels = useMemo(
    () => ({
      str: { short: 'name, phone', full: 'Tên, điện thoại' },
      str1: { short: 'course, ...', full: 'Tên khóa học' },
      date: { short: 'date', full: 'Ngày cập nhật / ghi chú' },
    }),
    [t]
  );

  // const [isSwitchChecked, setSwitchChecked] = useState(false);
  const isSwitchChecked = useMemo(() => {
      const input = dataSearch?.state?.filters || {};
      return input?.query?.val === true;
    }, [dataSearch?.state?.filters]);

  const {
    filters,
    filterDefault,
    handleGroupSelection,
    handleSearchHistory,
    handleCustomSelection,
  } = useGroupHandler();

  const commonProps = useMemo(() => ({
    dataStore,
    onPageChange: dataSearch.handlePageChange,
    onRowsPerPageChange: dataSearch.handleRowsPerPageChange,
    page: dataSearch.state.page,
    rowsPerPage: dataSearch.state.rowsPerPage,
  }), [dataStore, dataSearch]);

  const tableProps = useMemo(() => ({
    onDeselectAll: dataSelection.handleDeselectAll,
    onDeselectOne: dataSelection.handleDeselectOne,
    onSelectAll: dataSelection.handleSelectAll,
    onSelectOne: dataSelection.handleSelectOne,
    selected: dataSelection.selected,
    onItemClicked: dataSearch.handleNavigateChange,
  }), [dataSelection, dataSearch]);
    

  const handleDetail = (item) => {
    nextNavigate(
      router,
      dataSearch.state,
      paths.students.details,
      paths.students.index,
      { id: item.id, code: item.code, to: 'detail' },
    );
  };

  const handleTracking = (item) => {
    nextNavigate(
      router,
      dataSearch.state,
      paths.tracking.items,
      paths.students.index,
      { id: item.id, code: item.code, name: item.name, birthday: item.birthday },
    );
  };

  const handleCourse = (item) => {
    nextNavigate(
      router,
      dataSearch.state,
      paths.courses.details,
      paths.students.index,
      { id: item.courseId, name: item.courseName },
    );
  };

  const handleSwitchChange = (event) => {
    const { checked } = event.target;
    handleCustomSelection((prev) => ({
      ...prev,
      val: checked ? true : false
    }));
    // Or: handleCustomSelection({val: checked ? true : false});

    // if(checked){
    //   trackingRef.current?.reset?.();
    //   handleCustomSelection({val: true, arr: null});
    // }else{
    //   handleCustomSelection({val: null});
    // } 
  };

  const handleTrackingSelection = (arg) => {
    handleGroupSelection(arg);
    setOptions(arg?.status || []);

    statusRef.current?.reset?.();
    handleCustomSelection((prev) => ({
      ...prev,
      arr1: ""
    }));
  };

  const handleStatusSelection = (arg) => {
    let sTemp = ""; let label = '';
    if (arg) {
      label = arg.label;
      sTemp = `[${arg.value}][${label}]`;
    }
    handleCustomSelection((prev) => ({
      ...prev,
      arr1: sTemp
    }));
  };


  useEffect(() => {
    const input = dataSearch?.state?.filters;
    const trackingInfo = getFilterValue(input, 'arr');

    if (!trackingInfo) return;

    const array = dataCache();
    const trackingId = Number(trackingInfo.value); // ensure it's a number

    const matched = array.find((el) => el.value === trackingId);

    if (matched) {
      setOptions(matched.status);
    }
  }, []);


  useEffect(() => {
    const input = dataSearch?.state?.filters;
    const obj = getFilterValue(input, 'arr1');
    
    setHintValueStatus(obj?.label);
  }, [dataSearch?.state?.filters?.query]);

  return (
    <PageSection title="Students" rootRef={rootRef}>
      <Stack spacing={1}>
        <TitleButton
          label={isPhone ? '' : t(tokens.nav.students)}
          extraContent={
            <Stack direction="row" alignItems="center" spacing={2}>
              <Stack
                direction="row"
                alignItems="center"
              >
                <Typography color="text.primary" sx={{ mr: 0.5 }}>
                  {isPhone ? 'NGC' : 'Ngày ghi chú'}
                </Typography>
                <Switch
                  color="primary"
                  edge="start"
                  name="searchNew"
                  checked={isSwitchChecked}
                  onChange={handleSwitchChange}
                />
              </Stack>
              <Box sx={{ flex: 3 }}>
                <DropdownTracking 
                  ref={trackingRef}
                  label="Tiến độ"
                  name="trackingField"
                  onSelected={handleTrackingSelection}
                  hintValue={filterDefault || ''}
                  showClearIcon={true}
                />
              </Box>
              {!isPhone && (
              <Box sx={{ flex: 2 }}>
                <SearchSelectField
                  ref={statusRef}
                  label="Trạng thái"
                  name="statusField"
                  options={options || []}
                  onSelected={handleStatusSelection}
                  hintValue={hintValueStatus || ''}
                  showClearIcon={true}
                />
              </Box>
              )}
            </Stack>
          }
          sx={{
            width: {
              xs: 330,
              sm: 600,
            },
            textAlign: 'right',
          }}
        />
        <Card>
          <ListSearch
            {...({
              triggerSearch: filters,
              onHistory: handleSearchHistory,
            })}
            onFiltersChange={dataSearch.handleFiltersChange}
            filtersQuery={dataSearch.state.filters.query}
            placeholder={'str'}
            filterLabels={labels}
          />

          {isPhone ? (
            <ListView
              {...commonProps} onDetailClicked={handleDetail} 
            />
          ) : (
            <ListTable
              {...commonProps}
              {...tableProps}
              onDetailClicked={handleDetail} 
              onTrackingClicked={handleTracking}
              onCourseClicked={handleCourse}
            />
          )}
        </Card>
      </Stack>
    </PageSection>
  );
};

export default Page;
